import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import dataPrivacyService from '../../services/dataPrivacyService';
import { supabase } from '../../lib/supabase';

import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import EncryptionStatusDashboard from './components/EncryptionStatusDashboard';
import DataResidencyControls from './components/DataResidencyControls';
import PiiMaskingConfiguration from './components/PiiMaskingConfiguration';
import RetentionPolicyManagement from './components/RetentionPolicyManagement';

const DataPrivacySettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [storageMetrics, setStorageMetrics] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadStorageMetrics();

      // Subscribe to real-time changes for privacy preferences
      const channel = supabase
        ?.channel(`privacy-preferences-${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: `id=eq.${user?.id}`,
          },
          (payload) => {
            const updatedPreferences = payload?.new?.privacy_preferences;
            if (updatedPreferences) {
              setPreferences(updatedPreferences);
              toast?.info('Privacy settings updated', {
                description: 'Settings synced from another session',
              });
            }
          }
        )
        ?.subscribe();

      return () => {
        supabase?.removeChannel(channel);
      };
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const { data, error } = await dataPrivacyService?.getPrivacyPreferences(user?.id);
      if (error) throw error;

      const defaultPrefs = dataPrivacyService?.getDefaultPrivacyPreferences();
      setPreferences(data && Object.keys(data)?.length > 0 ? data : defaultPrefs);
    } catch (error) {
      console.error('Error loading privacy preferences:', error);
      toast?.error('Failed to load privacy settings');
      setPreferences(dataPrivacyService?.getDefaultPrivacyPreferences());
    } finally {
      setLoading(false);
    }
  };

  const loadStorageMetrics = async () => {
    try {
      const { data, error } = await dataPrivacyService?.getStorageMetrics(user?.id);
      if (error) throw error;
      setStorageMetrics(data);
    } catch (error) {
      console.error('Error loading storage metrics:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await dataPrivacyService?.updatePrivacyPreferences(user?.id, preferences);
      if (error) throw error;

      toast?.success('Privacy settings saved successfully', {
        description: 'Your privacy preferences have been updated',
      });
      
      // Reload storage metrics after save
      loadStorageMetrics();
    } catch (error) {
      console.error('Error saving privacy preferences:', error);
      toast?.error('Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = (section, updates) => {
    setPreferences((prev) => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        ...updates,
      },
    }));
  };

  const handleVerifyEncryption = async () => {
    try {
      const { data, error } = await dataPrivacyService?.verifyEncryptionStatus(user?.id);
      if (error) throw error;

      // Update local state with verification results
      updatePreferences('encryption', data);
      
      toast?.success('Encryption verified successfully', {
        description: 'All encryption protocols are active and valid',
      });
    } catch (error) {
      console.error('Error verifying encryption:', error);
      toast?.error('Failed to verify encryption status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-muted animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <NavigationSidebar />
      <div className="flex-1 ml-0 lg:ml-64">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-2">
                <img 
                  src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                  alt="DraftIQ Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground font-heading">
                  Data Privacy Settings
                </h1>
                <p className="text-muted-foreground">
                  Manage encryption, data residency, PII masking, and retention policies for compliance and security
                </p>
              </div>
            </div>
          </div>

          {/* Privacy Settings Modules */}
          <div className="space-y-6">
            {/* Encryption Status Dashboard */}
            <EncryptionStatusDashboard
              preferences={preferences}
              onVerify={handleVerifyEncryption}
            />

            {/* Data Residency Controls */}
            <DataResidencyControls
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* PII Masking Configuration */}
            <PiiMaskingConfiguration
              preferences={preferences}
              onUpdate={updatePreferences}
            />

            {/* Retention Policy Management */}
            <RetentionPolicyManagement
              preferences={preferences}
              storageMetrics={storageMetrics}
              onUpdate={updatePreferences}
            />

            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <Button
                onClick={handleSave}
                disabled={saving}
                iconName={saving ? 'Loader2' : 'Save'}
                iconPosition="left"
                className={saving ? 'animate-spin' : ''}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPrivacySettings;
